import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import {
  getCurrentTicketStatistics,
  getTicketPlanningGlobalStatistics,
  TicketStatistic,
  TicketStatsFilter,
} from '../api/ticket-stats.js';

export const TicketStatsPage = stateful(() => {
  const [search, setSearch] = useState('');
  const now = new Date();
  const [month, setMonth] = useState<number | undefined>(now.getMonth() + 1);
  const [year, setYear] = useState<number | undefined>(now.getFullYear());

  const globalStats = useObservable(
    () =>
      getTicketPlanningGlobalStatistics(
        month ?? 0,
        year ?? 0,
      )({
        simulationType: 1,
        plannedTicketsCount: 1,
      }),
    [month, year],
  );

  const dataviewOptions = buildFullOptions({
    class: TicketStatistic,
    operation: getCurrentTicketStatistics,
    customColumns: [
      { fieldName: 'ticketID', columnName: 'Ticket ID' },
      { fieldName: 'numAutomaticPlanning', columnName: 'Automatic Planning' },
      { fieldName: 'numAutomaticScheduling', columnName: 'Automatic Scheduling' },
      { fieldName: 'numManualPlanning', columnName: 'Manual Planning' },
      { fieldName: 'numManualScheduling', columnName: 'Manual Scheduling' },
      { fieldName: 'numTotalPlanning', columnName: 'Total Planning' },
      { fieldName: 'numTotalScheduling', columnName: 'Total Scheduling' },
      { fieldName: 'numRerunPlanning', columnName: 'Rerun Planning' },
    ],
    view: new View({
      filter: new TicketStatsFilter({
        search: search.trim() === '' ? undefined : search.trim(),
        month,
        year,
      }),
    }),
  });

  const globalStatsByType = new Map(
    (globalStats ?? []).map(s => [s.simulationType, s.plannedTicketsCount ?? 0] as const),
  );

  const numAutomaticDryRuns = globalStatsByType.get('automatic-dry-run') ?? 0;
  const numAutomaticRuns = globalStatsByType.get('automatic-run') ?? 0;
  const numRerunRuns = globalStatsByType.get('rerun') ?? 0;
  const numManualDryRuns = globalStatsByType.get('manual-dry-run') ?? 0;
  const numManualRuns = globalStatsByType.get('manual-run') ?? 0;

  const numAutomaticPlanning = numAutomaticDryRuns + numAutomaticRuns;
  const numAutomaticScheduling = numAutomaticRuns;
  const numRerunPlanning = numRerunRuns;
  const numManualPlanning = numManualDryRuns + numManualRuns;
  const numManualScheduling = numManualRuns;

  const globalStatCards = [
    {
      label: 'Automatic Planning',
      value: numAutomaticPlanning,
    },
    {
      label: 'Automatic Scheduling',
      value: numAutomaticScheduling,
    },
    {
      label: 'Manual Planning',
      value: numManualPlanning,
    },
    {
      label: 'Manual Scheduling',
      value: numManualScheduling,
    },
    {
      label: 'Total Planning',
      value: numAutomaticPlanning + numManualPlanning,
    },
    {
      label: 'Total Scheduling',
      value: numAutomaticScheduling + numManualScheduling,
    },
    {
      label: 'Rerun Planning',
      value: numRerunPlanning,
    },
  ] as const;

  return html`
    <div class="max-w-6xl mx-auto p-8">
      <div class="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 class="text-2xl font-bold">Ticket Stats</h2>
        </div>
      </div>

      <div class="w-full lg:w-auto mb-6">
        <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-[10rem_10rem_minmax(16rem,1fr)_auto] gap-2 items-end">
          <label class="form-control col-span-1">
            <div class="label py-0"><span class="label-text text-xs text-base-content/70">Month</span></div>
            <select
              class="select select-bordered w-full"
              .value=${String(month ?? 0)}
              @change=${(e: Event) => {
                const v = Number((e.target as HTMLSelectElement).value);
                setMonth(v === 0 ? undefined : v);
              }}
            >
              <option value="0">All</option>
              <option value="1">Jan</option>
              <option value="2">Feb</option>
              <option value="3">Mar</option>
              <option value="4">Apr</option>
              <option value="5">May</option>
              <option value="6">Jun</option>
              <option value="7">Jul</option>
              <option value="8">Aug</option>
              <option value="9">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>
          </label>

          <label class="form-control col-span-1">
            <div class="label py-0"><span class="label-text text-xs text-base-content/70">Year</span></div>
            <select
              class="select select-bordered w-full"
              .value=${String(year ?? 0)}
              @change=${(e: Event) => {
                const v = Number((e.target as HTMLSelectElement).value);
                setYear(v === 0 ? undefined : v);
              }}
            >
              <option value="0">All</option>
              ${Array.from({ length: 8 }, (_, i) => now.getFullYear() - 5 + i).map(
                y => html`<option value=${String(y)}>${y}</option>`,
              )}
            </select>
          </label>

          <label class="form-control">
            <div class="label py-0"><span class="label-text text-xs text-base-content/70">Search</span></div>
            <input
              type="search"
              class="input input-bordered w-full"
              placeholder="Search tickets..."
              .value=${search}
              @input=${(e: InputEvent) => setSearch((e.target as HTMLInputElement).value)}
            />
          </label>

          <button
            class="btn btn-ghost w-full sm:w-auto"
            @click=${() => {
              setSearch('');
              setMonth(now.getMonth() + 1);
              setYear(now.getFullYear());
            }}
            ?disabled=${search.trim() === '' && month === now.getMonth() + 1 && year === now.getFullYear()}
          >
            Reset
          </button>
        </div>
      </div>

      <div class="stats stats-vertical lg:stats-horizontal shadow bg-base-100 border border-neutral/10 mb-6 w-full">
        ${globalStatCards.map(card => {
          return html`
            <div class="stat">
              <div class="stat-title">${card.label}</div>
              <div class="stat-value text-primary">${card.value}</div>
              <div class="stat-desc text-base-content/60">Tickets (counted per run)</div>
            </div>
          `;
        })}
      </div>

      ${DataTable({ options: dataviewOptions.dataViewOptions })}
    </div>
  `;
});
